<template>
  <div class="min-h-screen bg-white">
    <Header />
    <main>

      <!-- ═══ HERO ═══ -->
      <section class="relative flex min-h-[90vh] items-center overflow-hidden bg-black pb-12 pt-20 md:pb-24 md:pt-24">

        <!-- Background video — randomly picked on mount -->
        <video
          v-if="heroVideo"
          :key="heroVideo"
          autoplay
          muted
          loop
          playsinline
          aria-hidden="true"
          class="absolute inset-0 h-full w-full object-cover"
        >
          <source :src="heroVideo" type="video/webm" />
        </video>

        <!-- Dark overlay — gradient: darker at top/bottom, lighter in centre -->
        <div
          class="pointer-events-none absolute inset-0"
          style="background: linear-gradient(to bottom, var(--overlay-heavy) 0%, var(--shadow-color-heavy) 45%, var(--overlay-heavy) 100%);"
          aria-hidden="true"
        />

        <!-- Content -->
        <div class="relative z-10 mx-auto w-full max-w-6xl px-4 md:px-6">
          <div class="mx-auto max-w-2xl text-center">

            <h1 class="text-5xl font-bold leading-[1.05] tracking-tight text-white md:text-6xl lg:text-7xl">
              Your pet got plans.
            </h1>


            <!-- Search bar — service-first two-step flow -->
            <form class="mt-8 w-full max-w-xl mx-auto" @submit.prevent="navigateToSearch">
              <div class="flex flex-col overflow-hidden rounded-full border border-black/10 bg-white shadow-xl sm:flex-row sm:items-stretch">

                <!--
                  Step 1: Service type — always visible, flex-1 so it fills
                  the pill while step 2 is collapsed.
                -->
                <ServicePicker
                  class="sm:flex-1 sm:min-w-[5.5rem]"
                  @select="onServiceSelect"
                  @clear="onServiceClear"
                />

                <!--
                  Step 2: City — GSAP-animated in after service type is selected.
                  v-if keeps it out of the DOM entirely until needed.
                -->
                <Transition :css="false" @enter="onCityPickerEnter">
                  <div
                    v-if="serviceSelected"
                    class="overflow-hidden sm:flex-[2] sm:min-h-[4rem] sm:border-l sm:border-black/8"
                  >
                    <CityPicker
                      ref="cityPickerRef"
                      v-model="locationQuery"
                      @select="onCitySelect"
                    />
                  </div>
                </Transition>

              </div>
            </form>



          </div>
        </div>

        <!-- Sentinel: when this scrolls off screen, header search bar appears -->
        <div ref="heroSentinelRef" class="pointer-events-none absolute bottom-0 left-0 h-px w-px" aria-hidden="true" />
      </section>


      <!-- ═══ FEATURED SERVICE CARDS ═══ -->
      <section class="bg-white px-4 pb-0 pt-10 md:px-6 md:pt-14">
        <div class="mx-auto max-w-6xl">
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">

            <!-- GROOMING -->
            <NuxtLink
              :to="$localeRoute('/services?type=grooming')"
              class="group relative flex min-h-[280px] overflow-hidden rounded-3xl bg-groom transition-transform hover:scale-[1.01] active:scale-[0.99]"
            >
              <!-- Text + CTA -->
              <div class="relative z-10 flex flex-col justify-between p-6 pb-7 md:p-7 md:pb-8" style="max-width: 58%;">
                <div>
                  <h3 class="text-xl font-bold leading-snug text-white md:text-2xl">
                    Your pet's next glow-up.
                  </h3>
                  <p class="mt-2 text-sm text-white/70">Book a groomer in minutes. Looks included.</p>
                </div>
                <span class="mt-5 inline-flex h-16 w-fit items-center rounded-full bg-groom-light px-7 text-xl font-semibold text-foreground transition-opacity group-hover:opacity-80">
                  Book Grooming Now
                </span>
              </div>
              <!-- Image -->
              <img
                src="/images/card-grooming.png"
                alt=""
                class="absolute bottom-0 right-0 z-20 h-[120%] w-auto max-w-[57%] object-contain object-bottom-right"
                aria-hidden="true"
              />
            </NuxtLink>

            <!-- VET VISIT -->
            <NuxtLink
              :to="$localeRoute('/services?type=vet')"
              class="group relative flex min-h-[280px] overflow-hidden rounded-3xl bg-vet transition-transform hover:scale-[1.01] active:scale-[0.99]"
            >
              <!-- Text + CTA -->
              <div class="relative z-10 flex flex-col justify-between p-6 pb-7 md:p-7 md:pb-8" style="max-width: 58%;">
                <div>
                  <h3 class="text-xl font-bold leading-snug text-white md:text-2xl">
                    Because vets matter.
                  </h3>
                  <p class="mt-2 text-sm text-white/70">Find a trusted vet near you. No stress.</p>
                </div>
                <span class="mt-5 inline-flex h-16 w-fit items-center rounded-full bg-vet-light px-7 text-xl font-semibold text-foreground transition-opacity group-hover:opacity-80">
                  Book Vet Now
                </span>
              </div>
              <!-- Image -->
              <img
                src="/images/card-vet.png"
                alt=""
                class="absolute bottom-0 right-0 z-20 h-[120%] w-auto max-w-[56%] object-contain object-bottom-right"
                aria-hidden="true"
              />
            </NuxtLink>

          </div>
        </div>
      </section>

      <!-- ═══ ALL SERVICES ═══ -->
      <section class="bg-muted/20 px-4 py-20 md:px-6 md:py-28">
        <div class="mx-auto max-w-6xl">
          <div class="mb-10 text-center">
            <h2 class="mb-3 text-5xl font-bold text-foreground md:text-6xl">
              {{ $t('landing.marketplace.title') }}
            </h2>
            <p class="mx-auto max-w-2xl text-base text-muted-foreground md:text-lg">
              {{ $t('landing.marketplace.subtitle') }}
            </p>
          </div>

          <!-- Row 1: 3 big cards -->
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-5">

            <!-- GROOMING -->
            <NuxtLink :to="$localeRoute('/services?type=grooming')" class="group flex aspect-square flex-col overflow-hidden rounded-3xl border border-border/50 bg-white p-4 transition-transform hover:scale-[1.005] active:scale-[0.99]">
              <div class="flex items-center justify-between gap-3 pb-4">
                <div>
                  <h3 class="text-xl font-bold text-foreground">{{ $t('landing.marketplace.grooming.title') }}</h3>
                  <p class="mt-0.5 text-sm text-muted-foreground">{{ $t('landing.marketplace.grooming.desc') }}</p>
                </div>
                <span class="shrink-0 h-11 inline-flex items-center rounded-full bg-muted px-5 text-sm font-semibold text-foreground">{{ $t('landing.marketplace.grooming.price') }}</span>
              </div>
              <div class="relative flex-1 overflow-hidden rounded-2xl bg-muted">
                <img src="/images/service-grooming.jpg" alt="" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            </NuxtLink>

            <!-- VET VISIT -->
            <NuxtLink :to="$localeRoute('/services?type=vet')" class="group flex aspect-square flex-col overflow-hidden rounded-3xl border border-border/50 bg-white p-4 transition-transform hover:scale-[1.005] active:scale-[0.99]">
              <div class="flex items-center justify-between gap-3 pb-4">
                <div>
                  <h3 class="text-xl font-bold text-foreground">{{ $t('landing.marketplace.vet.title') }}</h3>
                  <p class="mt-0.5 text-sm text-muted-foreground">{{ $t('landing.marketplace.vet.desc') }}</p>
                </div>
                <span class="shrink-0 h-11 inline-flex items-center rounded-full bg-muted px-5 text-sm font-semibold text-foreground">{{ $t('landing.marketplace.vet.price') }}</span>
              </div>
              <div class="relative flex-1 overflow-hidden rounded-2xl bg-muted">
                <img src="/images/service-vet.jpg" alt="" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            </NuxtLink>

            <!-- TRAINING -->
            <NuxtLink :to="$localeRoute('/services?type=training')" class="group flex aspect-square flex-col overflow-hidden rounded-3xl border border-border/50 bg-white p-4 transition-transform hover:scale-[1.005] active:scale-[0.99]">
              <div class="flex items-center justify-between gap-3 pb-4">
                <div>
                  <h3 class="text-xl font-bold text-foreground">{{ $t('landing.marketplace.training.title') }}</h3>
                  <p class="mt-0.5 text-sm text-muted-foreground">{{ $t('landing.marketplace.training.desc') }}</p>
                </div>
                <span class="shrink-0 h-11 inline-flex items-center rounded-full bg-muted px-5 text-sm font-semibold text-foreground">{{ $t('landing.marketplace.training.price') }}</span>
              </div>
              <div class="relative flex-1 overflow-hidden rounded-2xl bg-muted">
                <img src="/images/service-training.jpg" alt="" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            </NuxtLink>

          </div>

          <!-- Row 2: 3 smaller cards — identical structure -->
          <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:mt-5 lg:gap-5">

            <!-- DOG WALKING -->
            <NuxtLink :to="$localeRoute('/services?type=walking')" class="group flex aspect-square flex-col overflow-hidden rounded-3xl border border-border/50 bg-white p-4 transition-transform hover:scale-[1.005] active:scale-[0.99]">
              <div class="flex items-center justify-between gap-3 pb-4">
                <div>
                  <h3 class="text-xl font-bold text-foreground">{{ $t('landing.marketplace.walking.title') }}</h3>
                  <p class="mt-0.5 text-sm text-muted-foreground">{{ $t('landing.marketplace.walking.desc') }}</p>
                </div>
                <span class="shrink-0 h-11 inline-flex items-center rounded-full bg-muted px-5 text-sm font-semibold text-foreground">{{ $t('landing.marketplace.walking.price') }}</span>
              </div>
              <div class="relative flex-1 overflow-hidden rounded-2xl bg-muted">
                <img src="/images/service-walking.jpg" alt="" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            </NuxtLink>

            <!-- DAYCARE -->
            <NuxtLink :to="$localeRoute('/services?type=daycare')" class="group flex aspect-square flex-col overflow-hidden rounded-3xl border border-border/50 bg-white p-4 transition-transform hover:scale-[1.005] active:scale-[0.99]">
              <div class="flex items-center justify-between gap-3 pb-4">
                <div>
                  <h3 class="text-xl font-bold text-foreground">{{ $t('landing.marketplace.daycare.title') }}</h3>
                  <p class="mt-0.5 text-sm text-muted-foreground">{{ $t('landing.marketplace.daycare.desc') }}</p>
                </div>
                <span class="shrink-0 h-11 inline-flex items-center rounded-full bg-muted px-5 text-sm font-semibold text-foreground">{{ $t('landing.marketplace.daycare.price') }}</span>
              </div>
              <div class="relative flex-1 overflow-hidden rounded-2xl bg-muted">
                <img src="/images/service-daycare.jpg" alt="" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            </NuxtLink>

            <!-- PET SITTING -->
            <NuxtLink :to="$localeRoute('/services?type=petsitting')" class="group flex aspect-square flex-col overflow-hidden rounded-3xl border border-border/50 bg-white p-4 transition-transform hover:scale-[1.005] active:scale-[0.99]">
              <div class="flex items-center justify-between gap-3 pb-4">
                <div>
                  <h3 class="text-xl font-bold text-foreground">{{ $t('landing.marketplace.petsitting.title') }}</h3>
                  <p class="mt-0.5 text-sm text-muted-foreground">{{ $t('landing.marketplace.petsitting.desc') }}</p>
                </div>
                <span class="shrink-0 h-11 inline-flex items-center rounded-full bg-muted px-5 text-sm font-semibold text-foreground">{{ $t('landing.marketplace.petsitting.price') }}</span>
              </div>
              <div class="relative flex-1 overflow-hidden rounded-2xl bg-muted">
                <img src="/images/service-sitting.jpg" alt="" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            </NuxtLink>

          </div>

          <!-- Browse all services CTA -->
          <div class="mt-8 flex justify-center lg:mt-10">
            <NuxtLink :to="$localeRoute('/services')">
              <button class="inline-flex h-16 items-center rounded-full bg-foreground px-8 text-xl font-semibold text-background transition-opacity hover:opacity-80">
                {{ $t('landing.marketplace.viewAll') }}
              </button>
            </NuxtLink>
          </div>
        </div>
      </section>

      <!-- ═══ APP DOWNLOAD ═══ -->
      <section class="overflow-hidden bg-muted/40 px-4 py-20 md:px-6 md:py-28">
        <div class="mx-auto max-w-4xl text-center">
          <!-- Heading -->
          <h2 class="mb-10 font-national-park text-5xl font-bold leading-tight tracking-tight text-foreground md:text-6xl lg:text-7xl">
            Vets. Groomers.<br class="hidden sm:block" /> One tap away.
          </h2>

          <!-- App screenshot -->
          <div class="relative mx-auto mb-10 max-w-3xl">
            <img
              src="/app-demo.webp"
              alt="Bubba app on iPhone"
              class="w-full drop-shadow-2xl"
            />
          </div>

          <!-- Perks — 2×2 grid, icon left + text right -->
          <div class="mx-auto mb-12 grid max-w-3xl grid-cols-1 gap-x-10 gap-y-5 text-left sm:grid-cols-2">
            <div
              v-for="feature in appFeatures"
              :key="feature.text"
              class="flex items-center gap-4"
            >
              <Icon :name="feature.icon" size="28" class="shrink-0 text-foreground" />
              <span class="text-base font-semibold leading-snug text-foreground">{{ feature.text }}</span>
            </div>
          </div>

          <!-- Download buttons -->
          <div class="flex flex-wrap items-center justify-center gap-4">
            <AppStoreBadges :width="160" :gap="4" :vertical="false" />
          </div>
        </div>
      </section>

      <!-- ═══ FAQ ═══ -->
      <section class="px-4 py-20 md:px-6 md:py-28">
        <div class="mx-auto max-w-6xl">
          <div class="mb-10 text-center">
            <h2 class="mb-3 text-4xl font-bold text-foreground md:text-5xl">
              {{ $t('landing.faq.title') }}
            </h2>
          </div>

          <div class="divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/60">
            <div v-for="(faq, index) in faqs" :key="index">
              <button
                type="button"
                class="flex w-full items-center justify-between gap-6 px-6 py-5 text-left transition-colors hover:bg-muted/40"
                @click="openFaqs.has(index) ? openFaqs.delete(index) : openFaqs.add(index)"
              >
                <span class="text-lg font-semibold text-foreground md:text-xl">{{ faq.q }}</span>
                <Icon
                  name="tabler:chevron-down"
                  size="20"
                  class="shrink-0 text-foreground transition-transform duration-200"
                  :class="{ 'rotate-180': openFaqs.has(index) }"
                />
              </button>
              <Transition
                enter-active-class="transition-all ease-out duration-200"
                enter-from-class="opacity-0 max-h-0"
                enter-to-class="opacity-100 max-h-96"
                leave-active-class="transition-all ease-in duration-150"
                leave-from-class="opacity-100 max-h-96"
                leave-to-class="opacity-0 max-h-0"
              >
                <div v-if="openFaqs.has(index)" class="overflow-hidden border-t border-border/60 px-6 py-5">
                  <p class="text-base leading-relaxed text-foreground">{{ faq.a }}</p>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══ FOR BUSINESS ═══ -->
      <section class="bg-background px-4 py-20 md:py-28">
        <div class="mx-auto max-w-6xl">
          <div class="grid gap-10 lg:grid-cols-2 lg:gap-16 lg:items-center">
            <div>
              <h2 class="mb-6 text-5xl font-bold leading-tight text-foreground md:text-6xl">
                You do
                <span class="text-[--color-brand]">{{ displayedWord }}</span><span class="word-cursor text-[--color-brand]" :class="{ 'cursor-active': isTypingWord }">_</span><br />
                We help you grow.
              </h2>
              <ul class="mb-10 space-y-6">
                <li v-for="benefit in businessBenefits" :key="benefit.text" class="flex items-center gap-5">
                  <Icon :name="benefit.icon" size="28" class="shrink-0 text-foreground" />
                  <span class="text-base font-semibold text-foreground/80">{{ benefit.text }}</span>
                </li>
              </ul>
              <NuxtLink :to="$localeRoute('/partners')">
                <button class="inline-flex h-16 items-center rounded-full bg-foreground px-8 text-xl font-semibold text-background transition-opacity hover:opacity-80">
                  Start for free
                </button>
              </NuxtLink>
            </div>
            <div class="flex items-center justify-center">
              <img
                src="/console-demo.webp"
                alt="Bubba business console"
                class="w-full drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>


    </main>
    <Footer />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { gsap } from 'gsap';

// ── Hero video — random pick on every page load ──
const CDN_BASE = 'https://cdn.bubba.pet/landing/videos';
const HERO_VIDEOS_DESKTOP = [`${CDN_BASE}/hero-a.webm`, `${CDN_BASE}/hero-b.webm`, `${CDN_BASE}/hero-c.webm`, `${CDN_BASE}/01_hero_d.webm`];
const HERO_VIDEOS_MOBILE  = [`${CDN_BASE}/07_m_hero_a.webm`, `${CDN_BASE}/07_m_hero_b.webm`, `${CDN_BASE}/07_m_hero_c2.webm`, `${CDN_BASE}/07_m_hero_d.webm`];
const heroVideo = ref<string | null>(null);

// ── Service word typewriter ──
const serviceWords = ['Grooming', 'Vet Care', 'Walking', 'Daycare', 'Training', 'Boarding'];
let _wordIndex = 0;
const displayedWord = ref(serviceWords[0]!);
const isTypingWord = ref(false);
let wordTimerHandle: ReturnType<typeof setTimeout> | null = null;

function _clearWordTimer() {
  if (wordTimerHandle !== null) { clearTimeout(wordTimerHandle); wordTimerHandle = null; }
}

function _typewriterType() {
  const target = serviceWords[_wordIndex]!;
  if (displayedWord.value.length >= target.length) {
    isTypingWord.value = false;
    wordTimerHandle = setTimeout(_typewriterDelete, 2200);
    return;
  }
  displayedWord.value = target.slice(0, displayedWord.value.length + 1);
  wordTimerHandle = setTimeout(_typewriterType, 85 + Math.random() * 40);
}

function _typewriterDelete() {
  isTypingWord.value = true;
  if (displayedWord.value.length === 0) {
    _wordIndex = (_wordIndex + 1) % serviceWords.length;
    wordTimerHandle = setTimeout(_typewriterType, 120);
    return;
  }
  displayedWord.value = displayedWord.value.slice(0, -1);
  wordTimerHandle = setTimeout(_typewriterDelete, 55 + Math.random() * 25);
}
import { useRouter } from 'vue-router';
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import type { GeocodeResult } from '#types/geo';
import CityPicker from '#components/CityPicker.vue';
import ServicePicker from '#components/ServicePicker.vue';
import type { ServiceType } from '#components/ServicePicker.vue';
import { usePostHog } from '#composables/usePostHog';
import { useSchemaOrg, defineWebPage, defineWebSite, defineQuestion, useSeoMeta, useHead, useI18n, useRuntimeConfig, definePageMeta, useRoute, useState } from '#imports';

definePageMeta({
  layout: false,
  hideSupportFab: true,
});

const { $t, $ts, $localeRoute } = useI18n();
const { public: { siteUrl } } = useRuntimeConfig();
const route = useRoute();
const router = useRouter();
const { captureEvent } = usePostHog();

const canonicalUrl = () => `${siteUrl}${route.path === '/' ? '' : route.path}`;

useSeoMeta({
  title: () => $ts('seo.title'),
  description: () => $ts('seo.description'),
  ogTitle: () => $ts('seo.title'),
  ogDescription: () => $ts('seo.description'),
  ogType: 'website',
  ogUrl: () => canonicalUrl(),
  twitterCard: 'summary_large_image',
  twitterTitle: () => $ts('seo.title'),
  twitterDescription: () => $ts('seo.description'),
});

useHead({
  link: [{ rel: 'canonical', href: () => canonicalUrl() }]
});

const faqs = computed(() => {
  const items = $t('landing.faq.items');
  return Array.isArray(items) ? items : [];
});

useSchemaOrg(() => [
  defineWebPage({ '@type': 'CollectionPage' }),
  defineWebSite({
    potentialAction: {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }),
  // FAQ Rich Results
  ...faqs.value.map(faq => defineQuestion({
    name: faq.q,
    acceptedAnswer: faq.a,
  })),
]);

// ── Hero visibility — shared with Header to hide its search bar while hero is on screen ──
const heroSearchVisible = useState<boolean>('heroSearchVisible', () => false);
heroSearchVisible.value = true; // synchronous: hides header search immediately on SSR + client

const heroSentinelRef = ref<HTMLElement | null>(null);
let heroObserver: IntersectionObserver | null = null;

onMounted(() => {
  const pool = window.innerWidth < 768 ? HERO_VIDEOS_MOBILE : HERO_VIDEOS_DESKTOP;
  heroVideo.value = pool[Math.floor(Math.random() * pool.length)]!;

  if (!heroSentinelRef.value) return;
  heroObserver = new IntersectionObserver(
    ([entry]) => { heroSearchVisible.value = entry?.isIntersecting ?? false; },
    { threshold: 0 },
  );
  heroObserver.observe(heroSentinelRef.value as unknown as HTMLElement);

  wordTimerHandle = setTimeout(_typewriterDelete, 2200);
});

onBeforeUnmount(() => {
  heroObserver?.disconnect();
  heroSearchVisible.value = false;
  _clearWordTimer();
});

// ── Hero search ──
const locationQuery = ref('');
const serviceSelected = ref(false);
const selectedServiceKey = ref('');
const cityPickerRef = ref<InstanceType<typeof CityPicker> | null>(null);
const selectedCityCenter = ref<[number, number] | null>(null);

function onServiceSelect(service: ServiceType) {
  selectedServiceKey.value = service.key;
  serviceSelected.value = true;
  locationQuery.value = '';
  selectedCityCenter.value = null;
  captureEvent('hero_service_selected', { service: service.key });
  // Focus happens in onCityPickerEnter's GSAP onComplete callback
}

function onServiceClear() {
  selectedServiceKey.value = '';
  serviceSelected.value = false;
  locationQuery.value = '';
  selectedCityCenter.value = null;
}

function onCitySelect(result: GeocodeResult) {
  selectedCityCenter.value = result.center;
  captureEvent('hero_city_selected', { city: result.placeName ?? locationQuery.value });
  navigateToSearch();
}

function navigateToSearch() {
  const query: Record<string, string> = {};
  if (locationQuery.value.trim()) {
    query.city = locationQuery.value.trim();
  }
  if (selectedCityCenter.value) {
    query.lng = String(selectedCityCenter.value[0]);
    query.lat = String(selectedCityCenter.value[1]);
  }
  if (selectedServiceKey.value) {
    query.type = selectedServiceKey.value;
  }
  captureEvent('hero_search_submitted', {
    service: selectedServiceKey.value || undefined,
    city: locationQuery.value.trim() || undefined,
  });
  router.push({ path: '/services', query });
}

// ── Transition enter hook — GSAP slide-in for the CityPicker wrapper ──
function onCityPickerEnter(el: Element, done: () => void) {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    done();
    cityPickerRef.value?.focus();
    return;
  }
  const isDesktop = window.innerWidth >= 640;
  if (isDesktop) {
    gsap.fromTo(
      el,
      { maxWidth: 0, opacity: 0 },
      {
        maxWidth: 600,
        opacity: 1,
        duration: 0.42,
        ease: 'power3.out',
        onComplete: () => {
          done();
          cityPickerRef.value?.focus();
        },
      },
    );
  } else {
    gsap.fromTo(
      el,
      { opacity: 0, y: -8 },
      {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: 'power3.out',
        onComplete: () => {
          done();
          cityPickerRef.value?.focus();
        },
      },
    );
  }
}

// ── For business ──
const businessBenefits = [
  { icon: 'tabler:coin-off', text: 'No hidden fees, ever' },
  { icon: 'tabler:calendar-check', text: 'Smart calendar and booking management' },
  { icon: 'tabler:bell-ringing', text: 'Automated reminders — no-shows drop fast' },
  { icon: 'tabler:rosette-discount-check', text: 'Verified badge and review system' },
  { icon: 'tabler:chart-bar', text: 'Analytics and revenue insights' },
];

// ── App features ──
const appFeatures = [
  { icon: 'tabler:heart-rate-monitor', text: 'Health tracker — vaccines, meds & vet notes' },
  { icon: 'tabler:bell-ringing', text: 'Smart reminders for checkups and vaccines' },
  { icon: 'tabler:calendar-check', text: 'Book grooming and vet visits in seconds' },
  { icon: 'tabler:message-circle', text: 'Direct chat with your vet or groomer' },
];


// ── FAQ ──
const openFaqs = reactive(new Set<number>(faqs.value.map((_, i) => i)));
</script>

<style scoped>
/* ── Word slot — typewriter effect with blinking cursor ── */

.word-cursor {
  animation: cursor-blink 1s step-end infinite;
}

.word-cursor.cursor-active {
  animation: none;
  opacity: 1;
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}


</style>
